import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Download,
  Eye,
  FileVideo,
  Flame,
  Gauge,
  Loader2,
  Paperclip,
  Upload,
  Wrench,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  attachMultimediaToPuestaMarcha,
  bitacoraPhotoUrl,
  fetchPhotoMeta,
  uploadPuestaMarchaVideo,
  type PhotoMeta,
} from '@/features/mantenimiento/api';
import { authHeaders } from '@/shared/api/client';

/**
 * "Ver Puesta en Marcha" — read-only viewer of every drive-item the
 * puesta marcha persisted (instalación / llama / regulador) plus the
 * optional 4th multimedia slot. Mirrors the despacho viewer pattern:
 * Eye preview + Download per row, MIME-aware preview dialog.
 *
 * The multimedia slot is special: if the venta finalized without one,
 * the row turns into an upload affordance — pick a file and we run
 * the same direct-to-Graph chunked upload the puesta marcha dialog
 * uses, then patch `VideoMultimediaDriveItemID_RO`. The combustion
 * slots stay locked because the venta is already in `Cerrado`.
 */

type Slot = 'instalacion' | 'llama' | 'regulador' | 'multimedia';

const SLOT_META: Record<
  Slot,
  { label: string; icon: React.ElementType }
> = {
  instalacion: { label: 'Instalación', icon: Wrench },
  llama: { label: 'Flama', icon: Flame },
  regulador: { label: 'Regulador / Sopladores', icon: Gauge },
  multimedia: { label: 'Multimedia (opcional)', icon: Paperclip },
};

const SLOT_ORDER: Slot[] = ['instalacion', 'llama', 'regulador', 'multimedia'];

export function PuestaMarchaDetallesDialog({
  open,
  onClose,
  ventaId,
  instalacionId,
  llamaId,
  reguladorId,
  multimediaId,
}: {
  open: boolean;
  onClose: () => void;
  /** Required so the multimedia upload affordance can talk to the API. */
  ventaId: string;
  instalacionId: string | null | undefined;
  llamaId: string | null | undefined;
  reguladorId: string | null | undefined;
  multimediaId: string | null | undefined;
}) {
  const qc = useQueryClient();
  const slotIds: Record<Slot, string | null> = {
    instalacion: trim(instalacionId),
    llama: trim(llamaId),
    regulador: trim(reguladorId),
    multimedia: trim(multimediaId),
  };
  const presentSlots = SLOT_ORDER.filter((s) => slotIds[s] != null);
  // Multimedia is special — even when missing, we show its row so the
  // admin can drop a file in. Combustion slots stay hidden when null.
  const showMultimediaUpload = slotIds.multimedia == null;
  const [previewSlot, setPreviewSlot] = useState<Slot | null>(null);

  // Fetch metadata for every present slot in parallel so the rows
  // render the actual filename instead of just the slot label.
  const idsKey = presentSlots.map((s) => slotIds[s]!).join('|');
  const metaQ = useQuery({
    queryKey: ['puesta-marcha', 'metas', idsKey],
    queryFn: () =>
      Promise.all(presentSlots.map((s) => fetchPhotoMeta(slotIds[s]!))),
    enabled: open && presentSlots.length > 0,
    staleTime: 5 * 60_000,
  });
  const metas: Array<PhotoMeta | null> =
    metaQ.data ?? presentSlots.map(() => null);

  const onMultimediaAttached = () => {
    // Force the parent's header query to re-run so the dialog re-opens
    // with the new id and the upload row swaps for a normal SlotRow.
    qc.invalidateQueries({ queryKey: ['detalle-venta'] });
    qc.invalidateQueries({ queryKey: ['mantenimiento'] });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" aria-hidden />
            Puesta en Marcha
          </DialogTitle>
          <DialogDescription>
            Archivos cargados al finalizar la puesta en marcha.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {presentSlots.length === 0 && !showMultimediaUpload ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
              <FileVideo className="h-6 w-6" aria-hidden />
              Esta venta todavía no tiene archivos de puesta en marcha.
            </div>
          ) : (
            <ul className="divide-y rounded-lg border bg-card">
              {presentSlots.map((slot, i) => (
                <SlotRow
                  key={slot}
                  slot={slot}
                  driveItemId={slotIds[slot]!}
                  fileName={metas[i]?.fileName ?? null}
                  loading={metaQ.isPending}
                  onPreview={() => setPreviewSlot(slot)}
                />
              ))}
              {showMultimediaUpload && (
                <MultimediaUploadRow
                  ventaId={ventaId}
                  onAttached={onMultimediaAttached}
                />
              )}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
        {previewSlot != null && (
          <SlotPreviewDialog
            slot={previewSlot}
            driveItemId={slotIds[previewSlot]!}
            fileName={
              metas[presentSlots.indexOf(previewSlot)]?.fileName ?? null
            }
            onClose={() => setPreviewSlot(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SlotRow({
  slot,
  driveItemId,
  fileName,
  loading,
  onPreview,
}: {
  slot: Slot;
  driveItemId: string;
  fileName: string | null;
  loading: boolean;
  onPreview: () => void;
}) {
  const meta = SLOT_META[slot];
  const Icon = meta.icon;
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { url, mimeType } = await fetchPhoto(driveItemId);
      const fallback = `${slot}${extensionFromMime(mimeType)}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || fallback;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('No se pudo descargar el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };
  const displayName = fileName ?? meta.label;
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
      <span className="flex min-w-0 items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate font-medium text-foreground" title={displayName}>
            {loading && !fileName ? `${meta.label}…` : displayName}
          </span>
          {fileName && (
            <span className="block text-[11px] text-muted-foreground">
              {meta.label}
            </span>
          )}
        </span>
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onPreview}
          title="Vista previa"
          aria-label={`Vista previa ${meta.label}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Eye className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          title="Descargar"
          aria-label={`Descargar ${meta.label}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Download className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>
    </li>
  );
}

/**
 * Inline row that lets the admin add the optional multimedia file
 * after the venta has already finalized. Picking a file kicks off the
 * direct-to-Graph upload immediately (no submit button) and then
 * patches `VideoMultimediaDriveItemID_RO` via the attach endpoint.
 */
function MultimediaUploadRow({
  ventaId,
  onAttached,
}: {
  ventaId: string;
  onAttached: () => void;
}) {
  const meta = SLOT_META.multimedia;
  const Icon = meta.icon;
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'attaching'>('idle');
  const [progress, setProgress] = useState(0);
  const [pickedName, setPickedName] = useState<string | null>(null);
  const busy = phase !== 'idle';

  const onFilePicked = async (file: File) => {
    setPickedName(file.name);
    setPhase('uploading');
    setProgress(0);
    try {
      const result = await uploadPuestaMarchaVideo({
        ventaId,
        slot: 'multimedia',
        file,
        onProgress: ({ loaded, total }) => {
          if (total > 0) setProgress(loaded / total);
        },
      });
      if (result.outcome !== 'uploaded') {
        toast.error('No se pudo subir el archivo', { description: result.message });
        setPhase('idle');
        setPickedName(null);
        return;
      }
      setPhase('attaching');
      await attachMultimediaToPuestaMarcha(ventaId, result.driveItemId);
      toast.success('Multimedia agregada');
      setPhase('idle');
      setPickedName(null);
      setProgress(0);
      onAttached();
    } catch (err) {
      toast.error('No se pudo subir el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
      setPhase('idle');
      setPickedName(null);
      setProgress(0);
    }
  };

  return (
    <li className="flex flex-col gap-2 px-3 py-2.5 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">
            <span className="block truncate font-medium text-foreground">
              {pickedName ?? meta.label}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {phase === 'uploading'
                ? `Subiendo ${Math.round(progress * 100)}%…`
                : phase === 'attaching'
                  ? 'Vinculando archivo…'
                  : 'Adjuntá un video, imagen o PDF para incluirlo en la puesta en marcha.'}
            </span>
          </span>
        </span>
        <label
          className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border bg-background px-2.5 text-xs text-muted-foreground transition-colors hover:bg-secondary aria-disabled:pointer-events-none aria-disabled:opacity-50"
          aria-disabled={busy}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Upload className="h-3.5 w-3.5" aria-hidden />
          )}
          {busy ? 'Subiendo…' : 'Adjuntar'}
          <input
            ref={inputRef}
            type="file"
            accept="video/*,image/*,application/pdf"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.currentTarget.value = '';
              if (f) void onFilePicked(f);
            }}
          />
        </label>
      </div>
      {phase === 'uploading' && (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-150"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
    </li>
  );
}

function SlotPreviewDialog({
  slot,
  driveItemId,
  fileName,
  onClose,
}: {
  slot: Slot;
  driveItemId: string;
  fileName: string | null;
  onClose: () => void;
}) {
  const meta = SLOT_META[slot];
  const [src, setSrc] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const { url, mimeType: mt } = await fetchPhoto(driveItemId);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setSrc(url);
        setMimeType(mt);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Error desconocido');
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [driveItemId]);

  const isVideo = mimeType.startsWith('video/');
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const title = fileName ?? meta.label;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar el archivo. {error}
            </div>
          ) : !src ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : isVideo ? (
            <video
              src={src}
              controls
              className="mx-auto max-h-[70vh] w-full rounded-lg bg-black"
            />
          ) : isImage ? (
            <img
              src={src}
              alt={title}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          ) : isPdf ? (
            <iframe
              src={src}
              title={title}
              className="h-[70vh] w-full rounded-lg border"
            />
          ) : (
            <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
              No se puede previsualizar este tipo de archivo. Usá Descargar.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function fetchPhoto(driveItemId: string): Promise<{
  url: string;
  mimeType: string;
}> {
  const res = await fetch(bitacoraPhotoUrl(driveItemId), {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    mimeType: res.headers.get('content-type') ?? 'application/octet-stream',
  };
}

function extensionFromMime(mime: string): string {
  if (mime.startsWith('video/')) {
    if (mime === 'video/mp4') return '.mp4';
    if (mime === 'video/quicktime') return '.mov';
    if (mime === 'video/webm') return '.webm';
    return '.mp4';
  }
  if (mime === 'image/jpeg') return '.jpg';
  if (mime === 'image/png') return '.png';
  if (mime === 'image/webp') return '.webp';
  if (mime === 'application/pdf') return '.pdf';
  return '';
}

function trim(s: string | null | undefined): string | null {
  if (!s) return null;
  const t = String(s).trim();
  return t.length > 0 ? t : null;
}
